import { uploadDocToComment } from "@/api/comments/uploadDocToComment";
import { useMutation } from "@tanstack/react-query";
import { toast } from 'react-toastify';
import { useState } from 'react';

export const useUploadDocToComment = () => {
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

  const { isPending, mutateAsync: uploadDoc, error, isError, isSuccess } = useMutation({
    mutationKey: ["uploadDocumentToComment"],
    mutationFn: async (formData: FormData) => {
      const fileName = formData.get('file') as File;
      const fileId = `${fileName.name}-${Date.now()}`;
      
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
      
      try {
        const response = await uploadDocToComment(formData);
        
        // Simular progreso (en una implementación real, esto vendría del axios config)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const currentProgress = prev[fileId] || 0;
            if (currentProgress >= 90) {
              clearInterval(progressInterval);
              return { ...prev, [fileId]: 100 };
            }
            return { ...prev, [fileId]: currentProgress + 10 };
          });
        }, 100);
        
        // Limpiar progreso después de completar
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }, 1000);
        
        return response;
      } catch (error) {
        setUploadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[fileId];
          return newProgress;
        });
        
        // Manejo de errores más específico
        if (error instanceof Error) {
          if (error.message.includes('413') || error.message.includes('too large')) {
            toast.error('El archivo es demasiado grande. El tamaño máximo es 20MB.');
          } else if (error.message.includes('415') || error.message.includes('unsupported')) {
            toast.error('Tipo de archivo no soportado.');
          } else if (error.message.includes('network')) {
            toast.error('Error de conexión. Verifica tu conexión a internet.');
          } else {
            toast.error('Error al subir el archivo. Inténtalo nuevamente.');
          }
        }
        
        throw error;
      }
    },
  });

  return { 
    isPending, 
    uploadDoc, 
    error, 
    isError, 
    isSuccess,
    uploadProgress 
  };
};